import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getEstabelecimentos } from '../services/api';
import { getAuthToken, getAuthEstabelecimentosPermitidos } from '../services/authStorage';

const STORAGE_KEY = 'zen.estabelecimentoId';

const EstabelecimentoContext = createContext({
  estabelecimentos: [],
  estabelecimentoAtual: null,
  estabelecimentoId: null,
  carregando: true,
  selecionarEstabelecimento: () => {},
  recarregar: () => {},
});

export function EstabelecimentoProvider({ children }) {
  const [estabelecimentos, setEstabelecimentos] = useState([]);
  const [estabelecimentoId, setEstabelecimentoId] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? Number(raw) : null;
    } catch {
      return null;
    }
  });
  const [carregando, setCarregando] = useState(true);

  const recarregar = useCallback(async () => {
    if (!getAuthToken()) {
      setEstabelecimentos([]);
      setCarregando(false);
      return;
    }

    setCarregando(true);
    try {
      const { data } = await getEstabelecimentos();
      const listaBruta = Array.isArray(data) ? data : [];
      const permitidos = getAuthEstabelecimentosPermitidos();
      const lista = permitidos.length > 0
        ? listaBruta.filter((e) => permitidos.includes(Number(e.id)))
        : listaBruta;
      setEstabelecimentos(lista);

      const salvo = Number(localStorage.getItem(STORAGE_KEY));
      const ativos = lista.filter((e) => e.ativo !== false);
      const salvoOk = ativos.some((e) => Number(e.id) === salvo);
      const id = salvoOk
        ? salvo
        : (ativos[0]?.id ? Number(ativos[0].id) : (lista[0]?.id ? Number(lista[0].id) : null));
      setEstabelecimentoId(id);
      if (id) localStorage.setItem(STORAGE_KEY, String(id));
    } catch {
      setEstabelecimentos([]);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    recarregar();
  }, [recarregar]);

  const selecionarEstabelecimento = useCallback((id) => {
    const num = Number(id);
    setEstabelecimentoId(num || null);
    try {
      if (num) localStorage.setItem(STORAGE_KEY, String(num));
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }, []);

  const estabelecimentoAtual = useMemo(
    () => estabelecimentos.find((e) => Number(e.id) === Number(estabelecimentoId)) || null,
    [estabelecimentos, estabelecimentoId]
  );

  const value = useMemo(
    () => ({
      estabelecimentos,
      estabelecimentoAtual,
      estabelecimentoId,
      carregando,
      selecionarEstabelecimento,
      recarregar,
    }),
    [estabelecimentos, estabelecimentoAtual, estabelecimentoId, carregando, selecionarEstabelecimento, recarregar]
  );

  return (
    <EstabelecimentoContext.Provider value={value}>
      {children}
    </EstabelecimentoContext.Provider>
  );
}

export function useEstabelecimento() {
  return useContext(EstabelecimentoContext);
}

/** ID da filial selecionada para filtros de API (string vazia = todas permitidas). */
export function useEstabelecimentoFiltroApi() {
  const { estabelecimentoId } = useEstabelecimento();
  return estabelecimentoId || '';
}

export function getStoredEstabelecimentoId() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? Number(raw) : null;
  } catch {
    return null;
  }
}
